<template>
  <div>
    <b-container fluid class="mt-3">

      <!-- Toolbar -->
      <div class="px-3 pt-3">
        <div class="d-flex flex-wrap align-items-end justify-content-between mb-2">
          <div class="d-flex flex-wrap align-items-end">
            <div class="mr-3 mb-2">
              <label class="small mb-1 d-block">{{ $t('empresa') }}</label>
              <el-select
                v-model="empresaSeleccionada"
                :placeholder="$t('seleccione_empresa')"
                style="width:220px"
                filterable
                clearable
                :disabled="cargando"
                @change="onEmpresaChange"
              >
                <el-option v-for="empresa in empresas" :key="empresa.id" :label="empresa.nombre" :value="empresa.id" />
              </el-select>
            </div>

            <div class="mr-3 mb-2">
              <label class="small mb-1 d-block">{{ $t('traduccion_archivo') }}</label>
              <el-select
                v-model="archivoSeleccionado"
                :placeholder="$t('seleccione')"
                style="width:220px"
                filterable
                clearable
                :disabled="cargando || !empresaSeleccionada || cargandoArchivos"
              >
                <el-option v-for="archivo in archivos" :key="archivo.token" :label="archivo.nombre" :value="archivo.token" />
              </el-select>
            </div>

            <div class="mr-3 mb-2">
              <label class="small mb-1 d-block">{{ $t('buscar_en_bd') }}</label>
              <input
                type="text"
                class="form-control form-control-sm"
                style="width:260px;"
                v-model="search"
                :disabled="cargando"
                :placeholder="$t('traduccion_buscar_placeholder')"
                @keyup.enter="onBuscarClick"
              />
            </div>

            <div class="mr-3 mb-2 d-flex align-items-center" style="height:32px; margin-top:20px;">
              <b-form-checkbox v-model="soloCorregidas" :disabled="cargando">
                {{ $t('traduccion_solo_corregidas') }}
              </b-form-checkbox>
            </div>

            <div class="mr-3 mb-2" style="margin-top:20px;">
              <b-button size="sm" variant="primary" :disabled="cargando" @click="onBuscarClick">
                <i class="fas fa-search mr-1"></i> {{ $t('buscar') }}
              </b-button>
            </div>
          </div>

          <div class="mb-2">
            <b-button size="sm" variant="success" class="mr-2" :disabled="cargando || !rows.length" @click="exportarXls">
              <i class="fas fa-file-excel mr-1"></i> {{ $t('traduccion_exportar_xls') }}
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="abrirGlosario">
              <i class="fas fa-book mr-1"></i> {{ $t('traduccion_reglas') }}
            </b-button>
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="px-3">
        <div v-if="cargando" class="text-center py-4">
          <b-spinner small></b-spinner> {{ $t('cargando') }}
        </div>
        <div v-else-if="!rows.length" class="text-center text-muted py-4">
          {{ $t('sin_resultados') }}
        </div>
        <div v-else class="tabla-scroll-fixed tabla-maestro-compacta" style="margin-bottom:70px;">
          <table class="table table-bordered table-hover table-sm mb-0">
            <thead class="thead-sticky thead-light">
              <tr>
                <th class="th-ordenable" style="width:90px;" @click="ordenarPor('idMaterialETL')">
                  <input class="th-search-input" v-model="filterCol.idMaterialETL" @click.stop placeholder="" />
                  <span class="th-label">{{ $t('col_id') }} <i :class="iconoOrden('idMaterialETL')"></i></span>
                </th>
                <th class="th-ordenable" style="width:70px;" @click="ordenarPor('idioma')">
                  <input class="th-search-input" v-model="filterCol.idioma" @click.stop placeholder="" />
                  <span class="th-label">{{ $t('traduccion_idioma') }} <i :class="iconoOrden('idioma')"></i></span>
                </th>
                <th class="th-ordenable" style="min-width:200px;max-width:200px;" @click="ordenarPor('partName')">
                  <input class="th-search-input" v-model="filterCol.partName" @click.stop placeholder="" />
                  <span class="th-label">{{ $t('col_partName') }} <i :class="iconoOrden('partName')"></i></span>
                </th>
                <th class="th-ordenable" style="min-width:200px;max-width:200px;" @click="ordenarPor('partNameIdioma2')">
                  <input class="th-search-input" v-model="filterCol.partNameIdioma2" @click.stop placeholder="" />
                  <span class="th-label">{{ $t('traduccion_partname_idioma2') }} <i :class="iconoOrden('partNameIdioma2')"></i></span>
                </th>
                <th class="th-ordenable" style="min-width:250px;max-width:250px;" @click="ordenarPor('detalles')">
                  <input class="th-search-input" v-model="filterCol.detalles" @click.stop placeholder="" />
                  <span class="th-label">{{ $t('detalles') }} <i :class="iconoOrden('detalles')"></i></span>
                </th>
                <th class="th-ordenable" style="min-width:250px;max-width:250px;" @click="ordenarPor('detallesIdioma2')">
                  <input class="th-search-input" v-model="filterCol.detallesIdioma2" @click.stop placeholder="" />
                  <span class="th-label">{{ $t('traduccion_detalles_idioma2') }} <i :class="iconoOrden('detallesIdioma2')"></i></span>
                </th>
                <th style="width:70px;" class="text-center">{{ $t('guardar') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in processedRows" :key="row.idMaterialETL" :class="{ 'fila-modificada': row._dirty }">
                <td class="small text-muted">{{ row.idMaterialETL }}</td>
                <td class="text-center">
                  <select v-model="row.idioma" class="form-control form-control-sm" :disabled="!puedeEscribir" @change="marcarDirty(row)">
                    <option :value="null">NO</option>
                    <option value="ES">ES</option>
                    <option value="EN">EN</option>
                  </select>
                </td>
                <td style="min-width:200px;max-width:200px;">
                  <input v-model="row.partName" :title="row.partName" class="form-control form-control-sm"
                    :readonly="!puedeEscribir" @input="marcarDirty(row)" />
                </td>
                <td style="min-width:200px;max-width:200px;">
                  <input v-model="row.partNameIdioma2" :title="row.partNameIdioma2" class="form-control form-control-sm"
                    :readonly="!puedeEscribir" @input="marcarDirty(row)" />
                </td>
                <td style="min-width:250px;max-width:250px;">
                  <input v-model="row.detalles" :title="row.detalles" class="form-control form-control-sm"
                    :readonly="!puedeEscribir" @input="marcarDirty(row)" />
                </td>
                <td style="min-width:250px;max-width:250px;">
                  <input v-model="row.detallesIdioma2" :title="row.detallesIdioma2" class="form-control form-control-sm"
                    :readonly="!puedeEscribir" @input="marcarDirty(row)" />
                </td>
                <td class="text-center">
                  <button v-if="puedeEscribir" class="btn btn-sm btn-success" :class="{ 'btn-pulse': row._dirty && !row._saving }"
                    :disabled="!row._dirty || row._saving" @click="guardarRow(row)" :title="$t('guardar')">
                    <span v-if="row._saving" class="spinner-border spinner-border-sm"></span>
                    <i v-else class="fas fa-save"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div v-if="rows.length" class="pagination-floating-bar">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 text-secondary" style="font-size:.85rem;">{{ $t('registros_por_pagina') }}</label>
            <select v-model.number="pageSize" @change="onPageSizeChange(pageSize)" class="form-control form-control-sm" style="width:80px;" :disabled="cargando">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
              <option :value="0">{{ $t('todos') }}</option>
            </select>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="onPageChange(currentPage - 1)">
              <i class="fas fa-arrow-left mr-1"></i> {{ $t('anterior') }}
            </button>
            <span style="font-weight:600;color:#6c757d;font-size:.9rem;">
              {{ $t('pagina') }} {{ currentPage }} {{ $t('de') }} {{ totalPages }}
              <span class="text-muted ml-2" style="font-size:.8rem;">({{ processedRows.length }} / {{ total }} {{ $t('items_label') }})</span>
            </span>
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage >= totalPages" @click="onPageChange(currentPage + 1)">
              {{ $t('siguiente') }} <i class="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Modal Glosario -->
      <b-modal v-model="showGlosarioModal" :title="$t('traduccion_reglas')" size="xl" hide-footer>
        <div class="d-flex justify-content-end mb-2">
          <b-button size="sm" variant="success" :disabled="!glosarioItems.length" @click="exportarGlosarioXls">
            <i class="fas fa-file-excel mr-1"></i> {{ $t('traduccion_exportar_xls') }}
          </b-button>
        </div>
        <div v-if="puedeEscribir" class="border rounded p-2 mb-3">
          <div class="row">
            <div class="col-md-4">
              <label class="small mb-1 d-block">{{ $t('traduccion_termino') }}</label>
              <input type="text" class="form-control form-control-sm" v-model.trim="nuevoGlosario.termino" />
            </div>
            <div class="col-md-3">
              <label class="small mb-1 d-block">{{ $t('traduccion_tipo') }}</label>
              <select class="form-control form-control-sm" v-model="nuevoGlosario.tipo">
                <option value="Proteger">{{ $t('traduccion_tipo_proteger') }}</option>
                <option value="Reemplazar">{{ $t('traduccion_tipo_reemplazar') }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="small mb-1 d-block">{{ $t('traduccion_destino') }}</label>
              <input type="text" class="form-control form-control-sm" v-model.trim="nuevoGlosario.traduccionDestino"
                :disabled="nuevoGlosario.tipo === 'Proteger'" />
            </div>
            <div class="col-md-1 d-flex align-items-end">
              <b-button size="sm" variant="primary" :disabled="!nuevoGlosario.termino || glosarioGuardando" @click="crearGlosarioTermino">
                <i class="fas fa-plus"></i>
              </b-button>
            </div>
          </div>
        </div>

        <div v-if="glosarioCargando" class="text-center py-3"><b-spinner small></b-spinner></div>
        <template v-else>
          <div class="tabla-scroll-fixed tabla-maestro-compacta" style="max-height:400px; overflow-y:auto;">
            <table class="table table-bordered table-hover table-sm mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <th class="th-ordenable" @click="ordenarPorGlosario('termino')">
                    <input class="th-search-input" v-model="filterColGlosario.termino" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('traduccion_termino') }} <i :class="iconoOrdenGlosario('termino')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPorGlosario('tipo')">
                    <input class="th-search-input" v-model="filterColGlosario.tipo" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('traduccion_tipo') }} <i :class="iconoOrdenGlosario('tipo')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPorGlosario('traduccionDestino')">
                    <input class="th-search-input" v-model="filterColGlosario.traduccionDestino" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('traduccion_destino') }} <i :class="iconoOrdenGlosario('traduccionDestino')"></i></span>
                  </th>
                  <th style="width:70px;" class="text-center">{{ $t('activo') }}</th>
                  <th style="width:95px;" class="text-center">{{ $t('acciones') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in glosarioPaginado" :key="item.id" :class="{ 'fila-modificada': item._dirty }">
                  <td>
                    <input type="text" class="form-control form-control-sm" v-model.trim="item.termino"
                      :readonly="!puedeEscribir" @input="marcarDirtyGlosario(item)" />
                  </td>
                  <td>
                    <select class="form-control form-control-sm" v-model="item.tipo" :disabled="!puedeEscribir" @change="marcarDirtyGlosario(item)">
                      <option value="Proteger">{{ $t('traduccion_tipo_proteger') }}</option>
                      <option value="Reemplazar">{{ $t('traduccion_tipo_reemplazar') }}</option>
                    </select>
                  </td>
                  <td>
                    <input type="text" class="form-control form-control-sm" v-model.trim="item.traduccionDestino"
                      :readonly="!puedeEscribir" :disabled="item.tipo === 'Proteger'" @input="marcarDirtyGlosario(item)" />
                  </td>
                  <td class="text-center">
                    <div class="d-flex justify-content-center">
                      <b-form-checkbox v-model="item.activo" :disabled="!puedeEscribir" switch @change="marcarDirtyGlosario(item)" />
                    </div>
                  </td>
                  <td class="text-center">
                    <button v-if="puedeEscribir" class="btn btn-sm btn-success mr-1" :class="{ 'btn-pulse': item._dirty && !item._saving }"
                      :disabled="!item._dirty || item._saving" @click="guardarGlosarioTermino(item)" :title="$t('guardar')">
                      <span v-if="item._saving" class="spinner-border spinner-border-sm"></span>
                      <i v-else class="fas fa-save"></i>
                    </button>
                    <button v-if="puedeEscribir" class="btn btn-sm btn-danger" :disabled="item._deleting"
                      @click="eliminarGlosarioTermino(item)" :title="$t('eliminar')">
                      <span v-if="item._deleting" class="spinner-border spinner-border-sm"></span>
                      <i v-else class="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="glosarioItems.length" class="d-flex align-items-center justify-content-between mt-2 small">
            <div class="d-flex align-items-center">
              <label class="mr-2 mb-0 text-secondary">{{ $t('registros_por_pagina') }}</label>
              <select v-model.number="glosarioPageSize" @change="glosarioCurrentPage = 1" class="form-control form-control-sm" style="width:70px;">
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
            <div>
              <span class="text-muted mr-2">{{ $t('pagina') }} {{ glosarioCurrentPage }} {{ $t('de') }} {{ glosarioTotalPages }} ({{ processedGlosarioItems.length }})</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="glosarioCurrentPage === 1" @click="glosarioCurrentPage--"><i class="fas fa-chevron-left"></i></button>
              <button type="button" class="btn btn-sm btn-outline-secondary ml-1" :disabled="glosarioCurrentPage >= glosarioTotalPages" @click="glosarioCurrentPage++"><i class="fas fa-chevron-right"></i></button>
            </div>
          </div>
        </template>
      </b-modal>

    </b-container>
  </div>
</template>

<script>
import materialService from '@/api/materialService'
import utilsService from '@/api/utilsService'
import empresaService from '@/api/empresaService'
import { Select, Option } from 'element-ui'
import * as XLSX from 'xlsx'
import Swal from 'sweetalert2'

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 2500,
  timerProgressBar: true,
})

export default {
  name: 'TraduccionRevision',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      cargando: false,
      rows: [],
      total: 0,
      currentPage: 1,
      pageSize: 50,
      search: '',
      soloCorregidas: false,

      empresas: [],
      empresaSeleccionada: null,
      archivos: [],
      archivoSeleccionado: null,
      cargandoArchivos: false,

      // Búsqueda y orden por columna: client-side, sobre las filas de la página actual
      // (igual que el resto del admin — ver Inventario.vue, tipoVista "Inventario").
      filterCol: { idMaterialETL: '', idioma: '', partName: '', partNameIdioma2: '', detalles: '', detallesIdioma2: '' },
      ordenColumna: '',
      ordenDireccion: 'asc',

      showGlosarioModal: false,
      glosarioCargando: false,
      glosarioGuardando: false,
      glosarioItems: [],
      nuevoGlosario: { termino: '', tipo: 'Proteger', traduccionDestino: '' },
      filterColGlosario: { termino: '', tipo: '', traduccionDestino: '' },
      ordenColumnaGlosario: '',
      ordenDireccionGlosario: 'asc',
      glosarioCurrentPage: 1,
      glosarioPageSize: 20,
    };
  },
  computed: {
    rol() {
      return utilsService.getRol() || '';
    },
    puedeEscribir() {
      return utilsService.puedeEscribir() && this.rol !== 'ventas inex';
    },
    totalPages() {
      if (this.pageSize === 0) return 1;
      return Math.max(1, Math.ceil(Number(this.total || 0) / Number(this.pageSize || 50)));
    },
    processedRows() {
      let rows = Array.isArray(this.rows) ? this.rows.slice() : [];
      const f = this.filterCol;
      rows = rows.filter(r => {
        for (const k in f) {
          if (!Object.prototype.hasOwnProperty.call(f, k)) continue;
          const needle = String(f[k] == null ? '' : f[k]).trim().toLowerCase();
          if (!needle) continue;
          const vacioComoNo = k === 'idioma' ? 'NO' : '';
          const cell = String(r && r[k] != null && r[k] !== '' ? r[k] : vacioComoNo).toLowerCase();
          if (cell.indexOf(needle) === -1) return false;
        }
        return true;
      });
      if (this.ordenColumna) {
        const key = this.ordenColumna;
        const dir = this.ordenDireccion === 'asc' ? 1 : -1;
        rows.sort((a, b) => {
          const va = a && a[key] != null ? a[key] : '';
          const vb = b && b[key] != null ? b[key] : '';
          const na = parseFloat(va); const nb = parseFloat(vb);
          if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
          return String(va).localeCompare(String(vb)) * dir;
        });
      }
      return rows;
    },
    processedGlosarioItems() {
      let items = Array.isArray(this.glosarioItems) ? this.glosarioItems.slice() : [];
      const f = this.filterColGlosario;
      items = items.filter(it => {
        for (const k in f) {
          if (!Object.prototype.hasOwnProperty.call(f, k)) continue;
          const needle = String(f[k] == null ? '' : f[k]).trim().toLowerCase();
          if (!needle) continue;
          const cell = String(it && it[k] != null ? it[k] : '').toLowerCase();
          if (cell.indexOf(needle) === -1) return false;
        }
        return true;
      });
      if (this.ordenColumnaGlosario) {
        const key = this.ordenColumnaGlosario;
        const dir = this.ordenDireccionGlosario === 'asc' ? 1 : -1;
        items.sort((a, b) => {
          const va = a && a[key] != null ? a[key] : '';
          const vb = b && b[key] != null ? b[key] : '';
          return String(va).localeCompare(String(vb)) * dir;
        });
      }
      return items;
    },
    glosarioTotalPages() {
      return Math.max(1, Math.ceil(this.processedGlosarioItems.length / Number(this.glosarioPageSize || 20)));
    },
    glosarioPaginado() {
      const start = (this.glosarioCurrentPage - 1) * this.glosarioPageSize;
      return this.processedGlosarioItems.slice(start, start + this.glosarioPageSize);
    },
  },
  mounted() {
    this.cargarEmpresas();
    this.cargarDatos();
  },
  methods: {
    async cargarEmpresas() {
      const user = utilsService.getUserInfo() || {};
      const rol = (user.rol ? String(user.rol) : '').toLowerCase();
      let empresas = [];
      try {
        empresas = await empresaService.getEmpresasByUser();
      } catch (e) {
        empresas = [];
      }
      if ((!empresas || !empresas.length) && rol === 'admin') {
        try {
          empresas = await empresaService.getEmpresas();
        } catch (e) {
          empresas = [];
        }
      }
      this.empresas = (empresas || []).map(e => ({
        id: e && (e.id != null ? e.id : e.Id),
        nombre: e && (e.nombre != null ? e.nombre : e.Nombre),
      })).filter(e => e.id != null);
    },
    async onEmpresaChange() {
      this.archivoSeleccionado = null;
      this.archivos = [];
      if (!this.empresaSeleccionada) return;
      this.cargandoArchivos = true;
      try {
        const lista = await materialService.getLogsFiles(this.empresaSeleccionada);
        this.archivos = (Array.isArray(lista) ? lista : []).map(a => {
          const nombre = a.nombreXLSX || a.NombreXLSX || (a.token || a.Token);
          const fecha = this.formatFechaArchivo(a.createdAt || a.CreatedAt);
          return {
            token: a.token || a.Token,
            nombre: fecha ? `${nombre} (${fecha})` : nombre,
          };
        }).filter(a => a.token);
      } finally {
        this.cargandoArchivos = false;
      }
    },
    formatFechaArchivo(value) {
      if (!value) return '';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${dd}-${mm}-${d.getFullYear()}`;
    },
    async cargarDatos() {
      this.cargando = true;
      try {
        // pageSize = 0 ("Todos") no tiene un total conocido de antemano: se pide un tope
        // grande para traer todo de una vez en vez de paginar.
        const pageSizeApi = this.pageSize === 0 ? 1000000 : this.pageSize;
        const resp = await materialService.getTraduccionesParaRevision(
          this.currentPage, pageSizeApi, this.search, this.soloCorregidas,
          this.empresaSeleccionada, this.archivoSeleccionado
        );
        this.total = resp.total || 0;
        this.rows = (resp.items || []).map(r => ({ ...r, _dirty: false, _saving: false }));
      } finally {
        this.cargando = false;
      }
    },
    async onBuscarClick() {
      this.currentPage = 1;
      await this.cargarDatos();
    },
    async onPageChange(p) {
      const n = Math.max(1, Math.min(Number(p), this.totalPages));
      this.currentPage = n;
      await this.cargarDatos();
    },
    async onPageSizeChange(s) {
      this.pageSize = Math.max(0, Number(s));
      this.currentPage = 1;
      await this.cargarDatos();
    },
    exportarXls() {
      const rows = this.processedRows;
      if (!rows.length) {
        Swal.fire({ icon: 'info', title: this.$t('no_filas_exportar') });
        return;
      }
      const cols = [
        { key: 'idMaterialETL', label: this.$t('col_id') },
        { key: 'idioma', label: this.$t('traduccion_idioma') },
        { key: 'partName', label: this.$t('col_partName') },
        { key: 'partNameIdioma2', label: this.$t('traduccion_partname_idioma2') },
        { key: 'detalles', label: this.$t('detalles') },
        { key: 'detallesIdioma2', label: this.$t('traduccion_detalles_idioma2') },
      ];
      const header = cols.map(c => c.label);
      const data = [header, ...rows.map(r => cols.map(c => {
        if (c.key === 'idioma') return r.idioma || 'NO';
        return r[c.key] == null ? '' : r[c.key];
      }))];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = header.map(h => ({ wch: Math.max(10, String(h).length + 2) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Traducciones');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const fecha = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Traducciones_${fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
    },
    exportarGlosarioXls() {
      // this.glosarioItems no está paginado (solo processedGlosarioItems lo filtra/ordena):
      // exporta todo lo que calza con el filtro actual, sin recortar por la paginación del modal.
      const rows = this.processedGlosarioItems;
      if (!rows.length) {
        Swal.fire({ icon: 'info', title: this.$t('no_filas_exportar') });
        return;
      }
      const cols = [
        { key: 'termino', label: this.$t('traduccion_termino') },
        { key: 'tipo', label: this.$t('traduccion_tipo') },
        { key: 'traduccionDestino', label: this.$t('traduccion_destino') },
        { key: 'activo', label: this.$t('activo') },
      ];
      const header = cols.map(c => c.label);
      const data = [header, ...rows.map(r => cols.map(c => {
        if (c.key === 'activo') return r.activo ? 'SI' : 'NO';
        return r[c.key] == null ? '' : r[c.key];
      }))];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = header.map(h => ({ wch: Math.max(10, String(h).length + 2) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Reglas');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const fecha = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reglas_Traduccion_${fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
    },
    ordenarPor(columna) {
      if (this.ordenColumna === columna) {
        this.ordenDireccion = this.ordenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumna = columna;
        this.ordenDireccion = 'asc';
      }
    },
    iconoOrden(columna) {
      if (this.ordenColumna !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccion === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
    ordenarPorGlosario(columna) {
      if (this.ordenColumnaGlosario === columna) {
        this.ordenDireccionGlosario = this.ordenDireccionGlosario === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumnaGlosario = columna;
        this.ordenDireccionGlosario = 'asc';
      }
    },
    iconoOrdenGlosario(columna) {
      if (this.ordenColumnaGlosario !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccionGlosario === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
    marcarDirty(row) {
      if (!this.puedeEscribir) return;
      this.$set(row, '_dirty', true);
    },
    async guardarRow(row) {
      if (!this.puedeEscribir) return;
      this.$set(row, '_saving', true);
      try {
        await materialService.updateTraduccionRevision(row.idMaterialETL, {
          partName: row.partName,
          partNameIdioma2: row.partNameIdioma2,
          detalles: row.detalles,
          detallesIdioma2: row.detallesIdioma2,
          idioma: row.idioma || null,
        });
        row.traduccionRevision = false;
        this.$set(row, '_dirty', false);
        Toast.fire({ icon: 'success', title: this.$t('guardado') });
        // Al guardar la fila queda marcada como corregida (TraduccionRevision = 0): si la vista
        // está mostrando pendientes, ya no corresponde y desaparece de la lista.
        if (!this.soloCorregidas) {
          this.rows = this.rows.filter(r => r.idMaterialETL !== row.idMaterialETL);
          this.total = Math.max(0, this.total - 1);
        }
      } catch (e) {
        console.error('[guardarRow] error:', e);
        Toast.fire({ icon: 'error', title: this.$t('error_guardar_cambios') });
      } finally {
        this.$set(row, '_saving', false);
      }
    },

    async abrirGlosario() {
      this.showGlosarioModal = true;
      await this.cargarGlosario();
    },
    async cargarGlosario() {
      this.glosarioCargando = true;
      try {
        const items = await materialService.getGlosario(false);
        this.glosarioItems = (Array.isArray(items) ? items : []).map(it => ({ ...it, _dirty: false, _saving: false }));
        this.glosarioCurrentPage = 1;
      } finally {
        this.glosarioCargando = false;
      }
    },
    marcarDirtyGlosario(item) {
      if (!this.puedeEscribir) return;
      this.$set(item, '_dirty', true);
    },
    async crearGlosarioTermino() {
      if (!this.puedeEscribir || !this.nuevoGlosario.termino) return;
      this.glosarioGuardando = true;
      try {
        await materialService.crearGlosario({
          termino: this.nuevoGlosario.termino,
          tipo: this.nuevoGlosario.tipo,
          traduccionDestino: this.nuevoGlosario.tipo === 'Proteger' ? null : this.nuevoGlosario.traduccionDestino,
        });
        this.nuevoGlosario = { termino: '', tipo: 'Proteger', traduccionDestino: '' };
        await this.cargarGlosario();
        Toast.fire({ icon: 'success', title: this.$t('guardado') });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || this.$t('error_guardar_cambios');
        Swal.fire('Error', msg, 'error');
      } finally {
        this.glosarioGuardando = false;
      }
    },
    async guardarGlosarioTermino(item) {
      if (!this.puedeEscribir) return;
      this.$set(item, '_saving', true);
      try {
        await materialService.actualizarGlosario(item.id, {
          termino: item.termino,
          tipo: item.tipo,
          traduccionDestino: item.tipo === 'Proteger' ? null : item.traduccionDestino,
          activo: item.activo,
        });
        this.$set(item, '_dirty', false);
        Toast.fire({ icon: 'success', title: this.$t('guardado') });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || this.$t('error_guardar_cambios');
        Swal.fire('Error', msg, 'error');
        await this.cargarGlosario();
      } finally {
        this.$set(item, '_saving', false);
      }
    },

    async eliminarGlosarioTermino(item) {
      if (!this.puedeEscribir) return;
      const confirma = await Swal.fire({
        icon: 'warning',
        title: this.$t('confirmar_eliminar_registro'),
        text: this.$t('accion_irreversible'),
        showCancelButton: true,
        confirmButtonText: this.$t('eliminar'),
        cancelButtonText: this.$t('cancelar'),
        reverseButtons: true,
      });
      if (!confirma.isConfirmed) return;

      this.$set(item, '_deleting', true);
      try {
        await materialService.eliminarGlosario(item.id);
        this.glosarioItems = this.glosarioItems.filter(it => it.id !== item.id);
        Toast.fire({ icon: 'success', title: this.$t('eliminado') });
      } catch (e) {
        console.error('[eliminarGlosarioTermino] error:', e);
        Toast.fire({ icon: 'error', title: this.$t('no_se_pudo_eliminar') });
      } finally {
        this.$set(item, '_deleting', false);
      }
    },
  },
};
</script>

<style scoped>
.fila-modificada {
  background-color: #fff8e1;
}

.tabla-scroll-fixed {
  overflow-x: auto;
  position: relative;
  font-size: 0.8rem;
}
.tabla-scroll-fixed .table th,
.tabla-scroll-fixed .table td {
  font-size: 12px;
}

.thead-sticky {
  position: relative;
  z-index: 2;
  background: #f8f9fa;
}

.th-ordenable {
  cursor: pointer;
  user-select: none;
  vertical-align: top;
  white-space: nowrap;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}

.th-search-input {
  width: 100%;
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  margin-bottom: 3px;
  display: block;
  font-weight: normal;
}

.th-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.pagination-floating-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}

/* Mismo formato compacto que Inventario.vue (tipoVista "Inventario"/"Maestro de Materiales") */
.tabla-maestro-compacta .table th,
.tabla-maestro-compacta .table td {
  font-size: 11px;
  padding: 4px 6px;
}
.tabla-maestro-compacta .th-label {
  font-size: 11px;
}
.tabla-maestro-compacta .th-search-input {
  font-size: 10px;
}
.tabla-maestro-compacta .form-control-sm {
  font-size: 11px;
  padding: 2px 4px;
  height: calc(1.5em + 4px);
}
.tabla-maestro-compacta .btn-sm {
  font-size: 11px;
  padding: 2px 6px;
}
.tabla-maestro-compacta tr.fila-modificada > td {
  background-color: rgba(220, 53, 69, 0.08);
}
</style>
